'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import classnames from 'classnames';
import Image from 'next/image';
import { decodeJwt } from 'jose';
import RichTextParser from '../RichTextParser/RichTextParser';
import {
  hasValue,
  redirectTo,
  setCookie,
  VWR_REFERRER,
} from '@r-and-a-shared-ui/utils';
import { VWRConfiguration } from 'libs/core-data-mapping/src/lib/models';
import {
  QueueStatusEventData,
  useSignalRConnection,
} from '../../hooks/useSignalRConnection';
import { NO_ACCESS_PAGE } from '@r-and-a-shared-ui/auth-client';
import styles from './WaitingRoomComponent.module.scss';
import { handleVWRCacheBrowserNavigation } from '@r-and-a-shared-ui/core-data-mapping';
import { CacheConfig } from '@r-and-a-shared-ui/core-caching';
import { api } from '@r-and-a-shared-ui/randa-trpc-client';

export type WaitingRoomProps = {
  vwrConfig?: VWRConfiguration;
};

export function WaitingRoomComponent({ vwrConfig = {} }: WaitingRoomProps) {
  const {
    mainContent,
    title,
    queueNumberText,
    connectionErrorMessage,
    defaultContinuationUrl,
    realtimeEndpoint,
    exitCTALabel,
    logo,
  } = vwrConfig;

  const router = useRouter();
  const [userId, setUserId] = useState<string>('');
  const [queuePosition, setQueuePosition] = useState<number | undefined>();
  const [progressPercentage, setProgressPercentage] = useState<number>(0);
  const [verificationError, setVerificationError] = useState<
    string | undefined
  >();

  const normalizedUserId = useMemo(() => userId, [userId]);

  const { mutateAsync: verifyContinuationTokenAsync } =
    api.virtualWaitingRoomApi.verify_continuation_token.useMutation();

  useEffect(() => {
    if (!router.isReady) {
      return; // Wait for the router to be ready.
    }

    const token = router.query.continuation_token as string | undefined;

    if (!token) {
      setVerificationError('Missing continuation token');
      return;
    }

    // This async function handles the entire validation flow.
    const processToken = async () => {
      try {
        // 1. Decode the token to get the user ID
        const decoded = decodeJwt(token);
        const currentUserId = decoded.userId as string;

        if (!currentUserId) {
          throw new Error('User ID not found in token');
        }

        // Set the user ID to enable the SignalR connection
        setUserId(currentUserId);

        // 2. Verify the token with the backend
        const result = await verifyContinuationTokenAsync({
          token: token,
          cache: true,
          cacheTTL: CacheConfig.TTL.VirtualWaitingRoom,
        });

        if (!result.valid) {
          throw new Error('Token verification failed');
        }

        // If we reach here, the token is valid.
      } catch (err: any) {
        setVerificationError(
          err.message || 'An unknown verification error occurred',
        );
      }
    };

    processToken();
  }, [
    router.isReady,
    router.query.continuation_token,
    verifyContinuationTokenAsync,
  ]);

  useEffect(() => {
    if (verificationError) {
      router.push(NO_ACCESS_PAGE);
    }
  }, [verificationError, router]);

  const handleAdmitted = useCallback(() => {
    setCookie(VWR_REFERRER, window.document.referrer);
    redirectTo(defaultContinuationUrl, true);
  }, [defaultContinuationUrl]);

  const handleDisabledRoom = useCallback(() => {
    setCookie(VWR_REFERRER, window.document.referrer);
    window.history.go(-1);
  }, []);

  const handleQueuePositionEvent = useCallback(
    (eventData: QueueStatusEventData) => {
      if (!hasValue(eventData) && !hasValue(eventData.position)) {
        console.error('Queue position event data is invalid');
        return;
      }
      setQueuePosition(eventData.position);
      setProgressPercentage(eventData.progressPercentage || 0);
    },
    [],
  );

  const shouldConnect = Boolean(normalizedUserId && realtimeEndpoint);

  const { error, isConnected } = useSignalRConnection({
    userId: normalizedUserId,
    onAdmitted: handleAdmitted,
    onDisableRoom: handleDisabledRoom,
    onQueueStatusEvent: handleQueuePositionEvent,
    connectionErrorMessage,
    realtimeEndpoint,
    skip: !shouldConnect,
  });

  useEffect(() => {
    handleVWRCacheBrowserNavigation();
  }, []);

  return (
    <div className={styles['waiting-room']}>
      <div className={styles['waiting-room__box']}>
        {logo?.url && (
          <div className={styles['waiting-room__logo']}>
            <Image
              src={logo?.url}
              alt={logo?.alt || 'One Club Logo'}
              width={logo?.width}
              height={logo?.height}
              layout="intrinsic"
            />
          </div>
        )}
        {title && <h2 className={styles['waiting-room__title']}>{title}</h2>}
        {shouldConnect && (isConnected || error) && (
          <React.Fragment>
            {error && (
              <p className={styles['waiting-room__error-message']}>{error}</p>
            )}
            {!error && (
              <div className={styles['waiting-room__body']}>
                <div className={styles['progress-bar']}>
                  <div
                    className={styles['progress-bar__fill']}
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <div className={styles['waiting-room__queue']}>
                  {queueNumberText}
                  <span className={styles['waiting-room__queue-number']}>
                    {!hasValue(queuePosition) ? '...' : queuePosition}
                  </span>
                </div>

                {mainContent && (
                  <div className={styles['waiting-room__status-message']}>
                    <RichTextParser richText={mainContent} />
                  </div>
                )}
              </div>
            )}
            <button
              className={classnames(
                styles.btn,
                styles['btn--primary'],
                styles['waiting-room__cta'],
              )}
              onClick={handleDisabledRoom}
              type="button"
            >
              {exitCTALabel}
            </button>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

export default WaitingRoomComponent;
